import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/public/SEO";
import ArticleCard from "@/components/public/ArticleCard";
import BugunKapadokyaWidget from "@/components/public/BugunKapadokyaWidget";
import { ArrowRight, Mail } from "lucide-react";

const HERO_IMG = "/media/img/hero-poster.jpg";
const ABOUT_IMG = "/media/img/cave-interior.jpg";

export default function HomePage() {
  const [journal, setJournal] = useState([]);
  const [guides, setGuides] = useState([]);
  const [newsletter, setNewsletter] = useState({ email: "", loading: false, ok: false, error: "" });
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [j, g] = await Promise.all([
          api.get("/articles?lang=tr&type=journal&page_size=6"),
          api.get("/articles?lang=tr&type=guide&page_size=4"),
        ]);
        setJournal(j.data.items || []);
        setGuides(g.data.items || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    setNewsletter((s) => ({ ...s, loading: true, error: "" }));
    try {
      await api.post("/leads/newsletter", { email: newsletter.email, language: "tr", source_page: "/", consent: true });
      setNewsletter({ email: "", loading: false, ok: true, error: "" });
    } catch (err) {
      setNewsletter((s) => ({ ...s, loading: false, error: "Lütfen tekrar deneyin." }));
    }
  };

  const featured = journal[0];
  const rest = journal.slice(1, 5);

  return (
    <>
      <SEO
        title="Kapadokya Rehberi ve Günlüğü"
        description="Capella Cave, Kapadokya'nın vadilerini, mağara yaşamını ve benzersiz deneyimlerini bir araya getiren seçkin bir dijital rehber ve yakında hayata geçecek butik bir mağara otel vizyonudur."
        ogImage={HERO_IMG}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Capella Cave",
          url: process.env.REACT_APP_CANONICAL_URL || "https://capellacave.com",
          potentialAction: {
            "@type": "SearchAction",
            target: `${process.env.REACT_APP_CANONICAL_URL || "https://capellacave.com"}/arama?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        }}
      />

      {/* HERO — Cinematic Cappadocia video */}
      <section className="relative min-h-screen overflow-hidden grain" data-testid="home-hero">
        <video
          key={isMobile ? "mobile" : "desktop"}
          autoPlay muted loop playsInline preload="metadata"
          poster={HERO_IMG}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source
            src={isMobile ? "/media/video/hero-mobile.mp4" : "/media/video/hero-desktop.mp4"}
            type="video/mp4"
          />
        </video>
        {/* Overlay stack for cinematic readability */}
        {/* 1) overall darken — top + bottom heavier */}
        <div className="absolute inset-0 bg-gradient-to-b from-cc-dark/40 via-cc-dark/10 to-cc-dark/75 pointer-events-none" />
        {/* 2) top-left vignette — logo legibility */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 50% at top left, rgba(20,15,12,0.82) 0%, rgba(20,15,12,0.38) 45%, transparent 75%)",
          }}
        />
        {/* 3) bottom-left vignette — headline + CTA legibility */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 65% 60% at bottom left, rgba(20,15,12,0.85) 0%, rgba(20,15,12,0.4) 45%, transparent 78%)",
          }}
        />
        <div className="relative min-h-screen max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32 pt-32">
          <div className="cc-reveal max-w-4xl">
            <div className="flex items-center gap-3 text-[10px] tracking-[0.32em] uppercase text-cc-bg/85 mb-8">
              <span className="w-10 h-px bg-cc-sepia" />
              Capella Cave · Kapadokya
            </div>
            <h1 className="font-serif text-cc-bg font-light text-[2rem] sm:text-4xl md:text-5xl lg:text-[4.5rem] leading-[1.05] tracking-[-0.01em] mb-10">
              Kapadokya'yı bir turist gibi değil,<br/>
              <span className="italic font-light">bir hikâyenin içinden</span> keşfedin.
            </h1>
            <p className="text-cc-bg/85 text-base md:text-xl max-w-2xl leading-[1.7] mb-12 font-light">
              Capella Cave, Kapadokya'nın vadilerini, mağara yaşamını, yerel kültürünü ve benzersiz deneyimlerini
              bir araya getiren seçkin bir dijital rehber ve yakında hayata geçecek özel bir mağara otel vizyonudur.
            </p>
            <div className="flex flex-wrap items-center gap-5">
              <Link to="/kapadokya-rehberi" data-testid="hero-cta-primary" className="cc-btn cc-btn-light">
                Kapadokya'yı Keşfet
                <ArrowRight size={14} />
              </Link>
              <Link to="/kurucu-misafir-listesi" data-testid="hero-cta-secondary" className="cc-btn cc-btn-ghost border-cc-bg text-cc-bg hover:bg-cc-bg hover:text-cc-dark">
                Kurucu Misafir Listesi
              </Link>
            </div>
          </div>

          {/* Stat strip */}
          <div className="mt-20 pt-10 border-t border-cc-bg/20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl cc-reveal cc-reveal-delay-2">
            {[
              { v: "14", l: "Mağara Oda" },
              { v: "8", l: "Yıl Restorasyon" },
              { v: "60+", l: "Editör Rehberi" },
              { v: "2028", l: "Açılış" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-serif text-4xl md:text-5xl text-cc-bg leading-none mb-2">{s.v}</div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-cc-bg/55">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUGÜN KAPADOKYA - Daily freshness widget */}
      <BugunKapadokyaWidget />

      {/* MANIFESTO / INTRO */}
      <section className="py-28 md:py-40 max-w-6xl mx-auto px-6 md:px-12" data-testid="home-intro">
        <div className="grid md:grid-cols-12 gap-14 items-start">
          <div className="md:col-span-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-6">Manifesto</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-cc-text">
              Önce Kapadokya'nın hikâyesi. Sonra kapılarımız.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-3">
            <p className="text-cc-muted text-base md:text-lg leading-[1.85] mb-6">
              Capella Cave, 2028 ilkbaharında 14 odasıyla açılana kadar Kapadokya'nın en zengin editör arşivini oluşturuyor.
              Vadiler, gün doğumları, yerel ustalar, mevsimler — hepsi bu sayfalarda. Bir otelin tabelası yerine,
              bir bölgenin hafızasıyla başlıyoruz.
            </p>
            <p className="text-cc-muted text-base md:text-lg leading-[1.85]">
              Hazır olduğumuzda, sizi içeride bekliyor olacağız. Şimdilik, taşa ve toprağa kulak verelim.
            </p>
            <Link to="/capella-cave" className="inline-flex items-center gap-2 mt-10 text-[11px] tracking-[0.22em] uppercase text-cc-sepia hover:text-cc-text transition">
              Capella Cave Vizyonu <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* KAPADOKYA REHBERİ */}
      <section className="py-20 md:py-28 bg-white/40" data-testid="home-guide">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-4">Kapadokya Rehberi</div>
              <h2 className="font-serif text-4xl md:text-6xl text-cc-text leading-none">Başlamadan önce<br/><span className="italic">okuyun.</span></h2>
            </div>
            <Link to="/kapadokya-rehberi" className="cc-link text-xs tracking-[0.22em] uppercase">Tüm Rehberler →</Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {guides.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
          </div>
        </div>
      </section>

      {/* ÖNE ÇIKAN GÜNLÜK */}
      <section className="py-28 md:py-36 max-w-7xl mx-auto px-6 md:px-12" data-testid="home-journal">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-4">Kapadokya Günlüğü</div>
            <h2 className="font-serif text-4xl md:text-6xl text-cc-text leading-none">Editör Seçkisi</h2>
          </div>
          <Link to="/kapadokya-gunlugu" className="cc-link text-xs tracking-[0.22em] uppercase">Tüm Yazılar →</Link>
        </div>

        {featured && (
          <Link
            to={`/kapadokya-gunlugu/${featured.translations?.tr?.slug}`}
            data-testid="featured-article"
            className="cc-card group grid md:grid-cols-2 gap-10 md:gap-16 mb-24 items-center"
          >
            <div className="cc-card-img-wrap aspect-[4/5] md:aspect-[5/6] overflow-hidden">
              <img src={featured.featured_image_url} alt={featured.translations?.tr?.title || ""}
                   className="cc-card-img w-full h-full object-cover" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-4">
                {featured.category?.translations?.tr?.name || "Günlük"}
              </div>
              <h3 className="cc-card-title font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.05] text-cc-text mb-6">
                {featured.translations?.tr?.title}
              </h3>
              <p className="text-cc-muted leading-[1.85] mb-8 text-base md:text-lg">{featured.translations?.tr?.excerpt}</p>
              <span className="cc-link text-[11px] tracking-[0.22em] uppercase">Devamını Oku</span>
            </div>
          </Link>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
          {rest.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
        </div>
      </section>

      {/* CAPELLA CAVE YAKINDA */}
      <section className="relative bg-cc-dark text-cc-bg overflow-hidden grain" data-testid="home-hotel">
        <div className="absolute inset-0 opacity-50">
          <img src={ABOUT_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-cc-dark via-cc-dark/85 to-cc-dark/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-6">Yakında · Açılış 2028</div>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05] mb-8">
              Bir kapı,<br/><span className="italic">bin yıl önce</span>.
            </h2>
            <p className="text-cc-bg/80 text-base md:text-lg leading-[1.85] max-w-xl mb-10">
              Capella Cave, Göreme'nin yumuşak tüfüne sekiz yıllık bir restorasyonla oyulmuş 14 odalı bir butik otel.
              Hiçbir oda diğerine benzemez. Henüz açılmadık — ama Kurucu Misafir Listemize katılanlar ilk kapıdan girecek olanlar.
            </p>
            <div className="flex flex-wrap items-center gap-5">
              <Link to="/capella-cave" data-testid="home-hotel-cta" className="cc-btn cc-btn-light">
                Vizyonumuzu Okuyun <ArrowRight size={14} />
              </Link>
              <Link to="/kurucu-misafir-listesi" className="text-[11px] tracking-[0.22em] uppercase text-cc-bg hover:text-cc-sepia transition">
                Kurucu Misafir Listesi →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* KURUCU MİSAFİR CTA */}
      <section className="py-28 md:py-36 max-w-5xl mx-auto px-6 md:px-12 text-center" data-testid="home-founding-cta">
        <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-6">Sınırlı Davet</div>
        <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-cc-text leading-[1.05] mb-8">
          100 isim. 1 liste.
        </h2>
        <p className="text-cc-muted max-w-2xl mx-auto text-base md:text-lg leading-[1.85] mb-12">
          Capella Cave Kurucu Misafir Listesi'ne katılarak açılış öncesi öncelikli rezervasyon, açılış indirimi
          ve isminize ayrılmış özel deneyimler kazanın. Yer dolduğunda kayıtlar kapanır.
        </p>
        <Link to="/kurucu-misafir-listesi" data-testid="founding-cta-button" className="cc-btn">
          Listeye Katıl <ArrowRight size={14} />
        </Link>
        <p className="mt-6 text-xs text-cc-faint">Spam yok. Sadece önemli güncellemeler ve özel davetiyeler.</p>
      </section>

      {/* CAPELLA BÜLTENİ */}
      <section className="py-28 md:py-36 bg-cc-dark/[0.03] border-y border-cc-border" data-testid="home-newsletter">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Mail size={28} className="mx-auto text-cc-sepia mb-6" />
          <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-4">Capella Bülteni</div>
          <h2 className="font-serif text-3xl md:text-5xl text-cc-text mb-6">Kapadokya, kutunuzda.</h2>
          <p className="text-cc-muted mb-10 text-base md:text-lg leading-relaxed">
            Ayda bir, editör seçkisi. Yeni rehberler, mevsim notları, Capella Cave güncellemeleri.<br/>
            Sade, derin, gerçek.
          </p>
          <form onSubmit={submitNewsletter} className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto" data-testid="home-newsletter-form">
            <input
              type="email" required
              placeholder="e-posta@adresiniz.com"
              value={newsletter.email}
              onChange={(e) => setNewsletter((s) => ({ ...s, email: e.target.value }))}
              className="cc-input flex-1 text-center sm:text-left"
              data-testid="home-newsletter-email"
            />
            <button type="submit" disabled={newsletter.loading} className="cc-btn" data-testid="home-newsletter-submit">
              {newsletter.loading ? "..." : "Bültene Katıl"}
            </button>
          </form>
          {newsletter.ok && <p className="mt-6 text-sm text-cc-sepia" data-testid="home-newsletter-ok">Teşekkürler. Kutunuzu kontrol edin.</p>}
          {newsletter.error && <p className="mt-6 text-sm text-red-600">{newsletter.error}</p>}
        </div>
      </section>
    </>
  );
}
